import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Shield,
  Box,
  Package,
  Bug,
  Hammer,
  Gift,
  Briefcase,
  ListChecks,
  Crosshair,
  Pickaxe,
  LayoutGrid,
  Rocket,
  Server,
  Sparkles,
  Store,
  ShoppingBag,
  UploadCloud,
  Loader2,
  Activity,
  GitBranch,
  History,
  Zap,
  Database,
  Bot,
  Coins,
  Gem,
  Flag,
  SlidersHorizontal,
  Crown,
  Lock,
  Globe,
  FolderTree,
  Container,
  Gauge,
  Trophy,
  UserSearch,
  Footprints,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { spaceFromPath } from '@/stores/space.store';
import { usePendingRequestsStore } from '@/stores/pendingRequests.store';
import { useModuleEnabled } from '@/stores/projectPages.store';
import { GRADE_SETTINGS_FALLBACK } from '@/config/panelModules';
import { isProjectSettingsSection, settingsSectionOfPath } from '@/config/settingsSections';
import { DeployPanel } from '@/components/config/DeployPanel';
import { useGradeSettingsPlace } from '@/components/config/GradeCosmetics';
import { dashboardItem, navGroups, useNavItemVisible, type NavGroup, type NavItem } from './supervisionNav';
import styles from './Sidebar.module.scss';

/**
 * Navigation de l'espace Statistiques.
 *
 * <p>Ces sections étaient des onglets INTERNES à StatsPage, sous les onglets d'espace : deux
 * niveaux de navigation empilés pour la même chose. Elles remontent ici, et la section est portée
 * par l'URL (/stats/&lt;section&gt;), ce qui la rend partageable et met un favori sur une section
 * précise. /stats/players, qui n'était lié depuis nulle part et ne s'atteignait qu'en tapant
 * l'URL, y gagne aussi une entrée.</p>
 *
 * <p>Pays et Infrastructure n'ont AUCUNE source de données à ce jour : la place existe, la page le
 * dit explicitement plutôt que d'afficher un vide ambigu. Jobs est servi par le satellite (1.0.25).</p>
 */
const statsGroups: NavGroup[] = [
  {
    title: 'Économie',
    items: [
      // `end` sur /stats : sans lui il resterait allumé sur toutes les sous-routes, qui en dérivent.
      { path: '/stats', label: 'Monnaie', icon: Coins, feature: 'stats', page: 'stats', projectPage: 'stats', end: true,
        alsoActiveOn: ['/stats/economie'] },
      // Classement de l'argent : section du module « stats », comme Monnaie (route /api/stats/economy/top du satellite).
      { path: '/stats/classement', label: 'Classement', icon: Trophy, feature: 'stats', page: 'stats', projectPage: 'stats' },
      { path: '/stats/points', label: 'Points Boutique', icon: Gem, feature: 'stats', page: 'stats', projectPage: 'stats' },
      // Section « Boutique » (achats de RecubeBoutiqueMod, satellite 1.0.33) : module à part (statsShop), actif par défaut
      // sur Paladium seulement. Les clés et box vendues y sont, plus dans la section Box.
      { path: '/stats/boutique', label: 'Boutique', icon: ShoppingBag, feature: 'stats', page: 'stats', projectPage: 'statsShop' },
      // Section « Admin shop » : module à part (statsAdminShop), actif par défaut sur Paladium seulement.
      { path: '/stats/adminshop', label: 'Admin shop', icon: Store, feature: 'stats', page: 'stats', projectPage: 'statsAdminShop' },
      // Section « Box » (journal de PremiumMod) : module à part (statsBox), actif par défaut sur Paladium seulement.
      { path: '/stats/box', label: 'Box', icon: Gift, feature: 'stats', page: 'stats', projectPage: 'statsBox' },
    ],
  },
  {
    title: 'Gameplay',
    items: [
      { path: '/stats/jobs', label: 'Jobs', icon: Briefcase, feature: 'stats', page: 'stats', projectPage: 'stats' },
      // Section « Factions » (FactionApi) : module à part (statsGuilds), actif par défaut sur Paladium seulement.
      // Pas « statsFactions » : cette clé, plus ancienne, est celle de la section Pays de NationsGlory.
      { path: '/stats/factions', label: 'Factions', icon: Shield, feature: 'stats', page: 'stats', projectPage: 'statsGuilds' },
      // Section « Pays » : module à part (statsFactions, section de « stats »), actif par défaut sur
      // NationsGlory seulement, ouvrable à un autre projet dans Paramètres généraux > Fonctionnalités.
      { path: '/stats/pays', label: 'Pays', icon: Flag, feature: 'stats', page: 'stats', projectPage: 'statsFactions' },
    ],
  },
  {
    title: 'Joueurs',
    items: [
      { path: '/stats/activite', label: 'Activité', icon: Activity, feature: 'stats', page: 'stats', projectPage: 'stats' },
      // Page « Onboarding » (satellite 1.0.35) : inscriptions, parcours d'accès, intégration et conversion des nouveaux
      // joueurs. NationsGlory : lue sur le satellite qui annonce la capacité « onboarding » (le lobby).
      { path: '/stats/onboarding', label: 'Onboarding', icon: Footprints, feature: 'stats', page: 'stats', projectPage: 'stats' },
      { path: '/stats/players', label: 'Détail joueurs', icon: Users, feature: 'stats', page: 'stats', projectPage: 'stats' },
      // Section « Grades actifs » : module à part (statsGrades, section de « stats »), actif par défaut sur
      // Paladium seulement (liste des grades vendus du satellite), ouvrable ailleurs dans Fonctionnalités.
      { path: '/stats/grades', label: 'Grades actifs', icon: Crown, feature: 'stats', page: 'stats', projectPage: 'statsGrades' },
      // Section « Personas » (satellite 1.0.31) : module à part (statsPersonas), actif par défaut sur Paladium seulement.
      { path: '/stats/personas', label: 'Personas', icon: UserSearch, feature: 'stats', page: 'stats', projectPage: 'statsPersonas' },
    ],
  },
  {
    title: 'Technique',
    items: [
      { path: '/stats/infrastructure', label: 'Infrastructure', icon: Server, feature: 'stats', page: 'stats', projectPage: 'stats' },
    ],
  },
];

// Navigation de l'espace Configuration (édition in-game des packs). Même ordre que MODULE_ROUTES
// (config/panelModules) : le premier module ouvert est la cible de l'onglet Configuration.
const configGroups: NavGroup[] = [
  {
    // Le contenu des packs : blocs, items, entités, recettes.
    title: 'Contenu',
    items: [
      { path: '/config/blocks', label: 'Blocks', icon: Box, page: 'config.blocks', projectPage: 'blocks' },
      { path: '/config/items', label: 'Items', icon: Package, page: 'config.items', projectPage: 'items' },
      { path: '/config/entities', label: 'Entities', icon: Bug, page: 'config.entities', projectPage: 'entities' },
      { path: '/config/crafts', label: 'Crafts', icon: Hammer, page: 'config.crafts', projectPage: 'crafts' },
    ],
  },
  {
    // La config du jeu : lootbox et box, métiers, mondes, cosmétiques, shop, factions.
    title: 'Config',
    items: [
      // Box à clés et lootbox sans clé ; module « lootbox » et droit config.lootbox gardés tels quels.
      { path: '/config/lootbox', label: 'Lootbox / Box', icon: Gift, page: 'config.lootbox', projectPage: 'lootbox' },
      { path: '/config/jobs', label: 'Jobs', icon: Briefcase, page: 'config.jobs', projectPage: 'jobs' },
      // RecubeJobsMod (NationsGlory) : mod distinct de JobsMod, fichier du même nom, jamais fusionnés.
      // Droit et module à part ; actif par défaut sur NationsGlory seulement (défaut serveur).
      { path: '/config/recubejobs', label: 'RecubeJobs', icon: ListChecks, page: 'config.recubejobs', projectPage: 'recubejobs' },
      // Armes de RecubeICBM (NationsGlory) : droit et module à part, actif par défaut sur NationsGlory
      // seulement (défaut serveur), comme RecubeJobs.
      { path: '/config/weapons', label: 'Armes', icon: Crosshair, page: 'config.weapons', projectPage: 'weapons' },
      // Génération de minerais et régénération automatique (WorldOriginMod, NationsGlory) : droit et module à part,
      // actif par défaut sur NationsGlory seulement (défaut serveur), comme les Armes.
      { path: '/config/minerais', label: 'Minerais', icon: Pickaxe, page: 'config.minerais', projectPage: 'minerais' },
      // Carte des pays, géo-régions, niveaux et marqueurs de biomes, servie par le satellite du
      // projet (module « worlds ») : active par défaut sur NationsGlory seulement (défaut serveur).
      { path: '/config/worlds', label: 'Mondes', icon: Globe, page: 'config.worlds', projectPage: 'worlds' },
      // Catalogue des cosmétiques (création des ornements, rank tags, glyphes). Le choix de chaque
      // grade (ornement, tag, chat, boutique) est en Supervision > Grades, sous config.grades ; Grades
      // in-game coupé pour le projet, cet écran le reprend et l'entrée s'ouvre aussi à config.grades.
      { path: '/config/cosmetics', label: 'Cosmétiques des grades', icon: Sparkles, page: 'config.cosmetics',
        projectPage: 'cosmetics', pagesWhenOff: GRADE_SETTINGS_FALLBACK },
      { path: '/config/adminshop', label: 'Shop admin', icon: Store, page: 'config.adminshop', projectPage: 'adminshop' },
      { path: '/config/factions', label: 'Factions', icon: Flag, page: 'config.factions', projectPage: 'factions' },
    ],
  },
  {
    title: 'Validation',
    items: [
      // Historique des publications du panel, écarts entre les images, et demandes à valider. Présent
      // ici pour que le droit s'exerce sans donner l'espace Déploiement entier (réservé aux admins).
      { path: '/config/requests', label: 'Publications', icon: UploadCloud, page: 'config.requests', projectPage: 'requests' },
    ],
  },
  // Pack, Fichiers et Containers (ancienne catégorie Paramètres) sont dans les Paramètres généraux.
];

// Catégorie Discord (gestion du bot support) : admins, module « discord » actif pour le projet
// (par défaut Paladium seulement, bot mono-serveur). Une seule catégorie et ses items.
const discordGroup: NavGroup = {
  title: 'Discord',
  items: [
    { path: '/config/discord/bot', label: 'Configuration', icon: Bot, end: true },
    { path: '/config/discord/exploitation', label: 'Exploitation', icon: Activity, end: true },
  ],
};

/**
 * Navigation de l'espace Paramètres généraux (écrou du header). Sections dissociées de
 * « Grades & accès », qui ne garde que les utilisateurs et les grades. Chaque entrée n'apparaît que
 * si sa section est ouverte (settingsSection) : réglages du panel pour les admins globaux, Pack /
 * Fichiers / Containers du projet actif selon les droits de grade (config.files, config.containers).
 */
const settingsGroups: NavGroup[] = [
  {
    title: 'Paramètres généraux',
    items: [
      { path: '/parametres/fonctionnalites', label: 'Fonctionnalités', icon: SlidersHorizontal, settingsSection: 'fonctionnalites' },
      { path: '/parametres/admins', label: 'Admins', icon: Crown, settingsSection: 'admins' },
      { path: '/parametres/grades', label: 'Grades', icon: ShieldCheck, settingsSection: 'grades' },
      { path: '/parametres/projets', label: 'Projets', icon: Server, settingsSection: 'projets' },
      { path: '/parametres/grades-proteges', label: 'Grades protégés', icon: Lock, settingsSection: 'grades-proteges' },
    ],
  },
  {
    // Anciennement Configuration > Paramètres : réglages du projet choisi dans le bandeau.
    title: 'Packs & containers',
    items: [
      { path: '/parametres/packs', label: 'Pack', icon: LayoutGrid, settingsSection: 'packs' },
      { path: '/parametres/fichiers', label: 'Fichiers', icon: FolderTree, settingsSection: 'fichiers' },
      { path: '/parametres/containers', label: 'Containers', icon: Container, settingsSection: 'containers' },
    ],
  },
];

// Item d'accueil de l'espace Déploiement (produit repris du front CI/CD)
const deployHomeItem: NavItem = {
  path: '/deploiement',
  label: "Vue d'ensemble",
  icon: LayoutDashboard,
  end: true,
};

// Navigation de l'espace Déploiement
const deployGroups: NavGroup[] = [
  {
    title: 'Déploiement',
    items: [
      // « Déployer » = mise en prod (promotion), vit dans la vue Environnements (preprod → prod)
      { path: '/deploiement/environments/preprod', label: 'Déployer', icon: Rocket },
      // Suivi de la promotion, juste sous « Déployer » (son action de suivi)
      { path: '/deploiement/deployments', label: 'Suivi déploiement', icon: Activity },
      // « Release » = création/lancement de releases multi-containers (ancienne page « Déployer »)
      { path: '/deploiement/deploy', label: 'Release', icon: GitBranch, end: true },
      { path: '/deploiement/deploy/progress', label: 'Suivi des release', icon: Activity },
      { path: '/deploiement/history', label: 'Historique', icon: History },
    ],
  },
  {
    title: 'Générale',
    items: [
      { path: '/deploiement/environments', label: 'Environnement', icon: Server },
      { path: '/deploiement/containers', label: 'Container MC', icon: Package },
      { path: '/deploiement/apis', label: 'Apis', icon: Zap },
      { path: '/deploiement/database', label: 'Base de données', icon: Database },
      { path: '/deploiement/resource-packs', label: 'Resource pack', icon: Sparkles },
    ],
  },
  {
    // Santé des serveurs de jeu vue par le moteur (profileur de tick). Lu dans le satellite du projet.
    title: 'Diagnostic',
    items: [
      { path: '/deploiement/profileur', label: 'Profileur', icon: Gauge },
    ],
  },
  {
    // Réseau du projet actif (ssl-manager via le CICD). Le commun à tous les projets (nodes, Cloudflare,
    // serveurs externes, domaines principaux) est dans Paramètres > Réseau.
    title: 'Réseau',
    items: [
      { path: '/deploiement/reseau/domaines', label: 'Domaines', icon: Globe },
      { path: '/deploiement/reseau/certificats', label: 'Certificats SSL', icon: ShieldCheck },
    ],
  },
  {
    title: 'Validation',
    items: [
      { path: '/deploiement/requests', label: 'Publications', icon: UploadCloud },
    ],
  },
];

export function Sidebar({ open = false }: { open?: boolean }) {
  const { user } = useAuthStore();
  const activeGameId = useGameStore((s) => s.activeGameId);
  // Global ET projet ET parent, pour le projet actif ; se redessine quand les réglages arrivent.
  const isModuleOn = useModuleEnabled();
  const pathname = useLocation().pathname;
  const space = spaceFromPath(pathname);
  const navigate = useNavigate();
  const [deployOpen, setDeployOpen] = useState(false);
  const pendingCount = usePendingRequestsStore((s) => s.count);
  const processing = usePendingRequestsStore((s) => s.processing);
  const fetchPending = usePendingRequestsStore((s) => s.fetch);
  const gradeSettingsPlace = useGradeSettingsPlace();
  // « Envoyer » : en Configuration, sur Pack / Fichiers / Containers des Paramètres, où s'éditent
  // aussi des fichiers de pack et de containers (un grade qui n'a que config.files y envoie ses
  // modifications sans passer par l'espace Configuration), et sur Supervision > Grades quand le
  // détail d'un grade y écrit ses cosmétiques et sa boutique dans les containers (un grade qui n'a
  // que config.grades n'a pas d'autre bouton pour les envoyer).
  const showSend = space === 'configuration'
    || (space === 'parametres' && isProjectSettingsSection(settingsSectionOfPath(pathname)))
    || (gradeSettingsPlace === 'supervision' && (pathname === '/grades' || pathname.startsWith('/grades/')));

  // Compteur de demandes en attente (badge sur « Publications ») : au montage + toutes les 30 s.
  useEffect(() => {
    fetchPending(activeGameId);
    const t = setInterval(() => fetchPending(activeGameId), 30000);
    return () => clearInterval(t);
  }, [activeGameId, fetchPending]);

  // Droits, modules, sections des Paramètres, fonctionnalités du jeu, et capacités du satellite du
  // groupe actif : une page de Supervision que ce satellite ne sert pas est masquée (supervisionNav).
  const canShow = useNavItemVisible();

  const renderLink = (item: NavItem) => {
    const Icon = item.icon;
    const alsoActive = (item.alsoActiveOn ?? []).some((p) => pathname.startsWith(p));
    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          end={item.end}
          className={({ isActive }) => `${styles.navLink} ${isActive || alsoActive ? styles.active : ''}`}
        >
          <span className={styles.navIcon}>
            <Icon size={20} strokeWidth={2} />
          </span>
          <span className={styles.navLabel}>{item.label}</span>
          {item.path.endsWith('/requests') && (
            processing ? (
              <Loader2 size={16} className={styles.navSpin} />
            ) : pendingCount > 0 ? (
              <span className={styles.navBadge}>{pendingCount}</span>
            ) : null
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : ''}`}>
      <div className={styles.logo}>
        <img src="/recube-logo.png" alt="Recube" className={styles.logoImg} />
      </div>

      <nav className={styles.nav}>
        {space === 'deploiement' ? (
          <>
            <ul className={styles.navList}>{renderLink(deployHomeItem)}</ul>
            {deployGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        ) : space === 'parametres' ? (
          <>
            {settingsGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        ) : space === 'statistiques' ? (
          <>
            {statsGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        ) : space === 'configuration' ? (
          <>
            {configGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
            {/* Catégorie Discord : admin et module « discord » actif (global ET projet). Son défaut
                serveur reconduit l'ancien comportement, Paladium seulement. */}
            {user?.isAdmin && isModuleOn('discord') && (
              <div className={styles.navGroup}>
                <span className={styles.navGroupTitle}>{discordGroup.title}</span>
                <ul className={styles.navList}>{discordGroup.items.map(renderLink)}</ul>
              </div>
            )}
          </>
        ) : (
          <>
            {canShow(dashboardItem) && <ul className={styles.navList}>{renderLink(dashboardItem)}</ul>}
            {navGroups.map((group) => {
              const items = group.items.filter(canShow);
              if (items.length === 0) return null;
              return (
                <div key={group.title} className={styles.navGroup}>
                  <span className={styles.navGroupTitle}>{group.title}</span>
                  <ul className={styles.navList}>{items.map(renderLink)}</ul>
                </div>
              );
            })}
          </>
        )}
      </nav>

      {showSend && (
        <>
          <button className={styles.deployBtn} onClick={() => setDeployOpen(true)}>
            <span className={styles.navIcon}><UploadCloud size={20} strokeWidth={2} /></span>
            <span className={styles.navLabel}>Envoyer</span>
          </button>
          <DeployPanel open={deployOpen} onClose={() => setDeployOpen(false)} />
        </>
      )}

      {space === 'deploiement' && (
        <div className={styles.deployActions}>
          <button className={styles.deployBtn} onClick={() => navigate('/deploiement/environments/preprod')}>
            <span className={styles.navIcon}><Rocket size={20} strokeWidth={2} /></span>
            <span className={styles.navLabel}>Déployer</span>
          </button>
          <button className={[styles.deployBtn, styles.releaseBtn].join(' ')} onClick={() => navigate('/deploiement/deploy')}>
            <span className={styles.navIcon}><GitBranch size={20} strokeWidth={2} /></span>
            <span className={styles.navLabel}>Release</span>
          </button>
        </div>
      )}

      {user && (
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.gamertag || 'User'} />
            ) : (
              <span>{user.gamertag?.charAt(0).toUpperCase() || '?'}</span>
            )}
          </div>
          <div className={styles.userDetails}>
            <span className={styles.userName}>{user.gamertag || 'Utilisateur'}</span>
            <span className={styles.userRole}>{user.displayRole || user.role}</span>
          </div>
        </div>
      )}
    </aside>
  );
}
